/**
 * The editor's background pixel analysis: «Güvenli alanı otomatik bul» and «Çakışmayı ölç (5 değer)».
 *
 * SOURCE NOTE — the task names `m08c-library.js`, but that module is the library screen and the
 * project transfer envelope; it holds none of these. All of them live in `m01b-assets.js`, in the
 * section headed «piksel analizi (şekil varsayımı yok)»: `analyzeAsset`, `inkInBox`, `artworkBBox`,
 * `detectSafeBand`, `layerFromBand` and `verifyLayerPlacement` (`m01b-assets.js:510-595`).
 * `m08-admin.js:383-410` is the call site that binds them to the two chips. The fourth function is
 * upstream's `verifyLayerPlacement`, not `checkFit` — `checkFit` is the chip's DOM id
 * (`m08-admin.js:342`), so it is exported under both names and the source name is the definition.
 *
 * The section's own title states the rule the port has to keep: no shape assumption. Nothing here
 * knows what a badge, a frame or a seal looks like. It reads the artwork's pixels, finds the widest
 * row range that carries no ink, and reports what it measured — which is why the help text under
 * the chips promises exactly that and nothing more.
 */
import {
  type Canvas,
  canvasH,
  canvasW,
  clamp,
  type Fit,
  fitText,
  type Layer,
  type Layout,
} from './render/engine';
import { type AssetRef, resolveAssetRef } from './render/library';

// ------------------------------------------------------------------------------- the ink profile

/**
 * A background reduced to a 320px-wide occupancy grid.
 *
 * `w`/`h` stay the artwork's real pixel size because `layerFromBand` needs its aspect ratio to turn
 * a height ratio into a font size ratio; `gw`/`gh` are the grid the mask is indexed by.
 */
export interface AssetAnalysis {
  w: number;
  h: number;
  gw: number;
  gh: number;
  /** One byte per grid cell, row-major: 1 where the cell carries ink. */
  mask: Uint8Array;
}

export interface InkBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface InkMeasure {
  ink: number;
  total: number;
  ratio: number;
}

export interface ArtworkBBox {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** A horizontal band, in 0–1 ratios of the canvas — what `detectSafeBand` hands the editor. */
export interface SafeBand {
  top: number;
  bottom: number;
  center: number;
  height: number;
  left: number;
  right: number;
  width: number;
}

export interface BandSafeArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The layer fields «Güvenli alanı otomatik bul» writes, plus the safe area it registers. */
export interface BandLayerConfig {
  x: number;
  y: number;
  w: number;
  fontSizeRatio: number;
  minFontSizeRatio: number;
  lineHeight: number;
  maxLines: number;
  safeArea: BandSafeArea;
}

export interface SafeBandOptions {
  minY?: number | undefined;
  maxY?: number | undefined;
  centerLeft?: number | undefined;
  centerRight?: number | undefined;
}

const analysisCache = new Map<string, AssetAnalysis>();

/** `m01b-assets.js:246` — the same detached-image loader the ingestion path uses. */
function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Görsel çözülemedi'));
    im.src = src;
  });
}

/**
 * Draw the background into a detached canvas and reduce it to the ink mask.
 *
 * Cached by `cacheKey` — the asset id at the call site (`m08-admin.js:390`) — because the chip can
 * be pressed repeatedly on an unchanged background and the asset is immutable, so the same id can
 * never mean different bytes. The fallback key is the data URL's last 64 characters, as upstream.
 *
 * The two constants are the reference's: a pixel under alpha 24 is not there at all, and one above
 * 243 on all three channels is paper rather than ink, so a white-on-white certificate body reads as
 * empty space and a band can be found inside it.
 */
export async function analyzeAsset(dataUrl: string, cacheKey?: string): Promise<AssetAnalysis> {
  const key = cacheKey || String(dataUrl).slice(-64);
  const hit = analysisCache.get(key);
  if (hit) return hit;
  const im = await loadImageEl(dataUrl);
  const W = 320;
  const H = Math.max(1, Math.round((320 * im.naturalHeight) / im.naturalWidth));
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d', { willReadFrequently: true });
  /* Upstream cannot fail here and does not test for it; the editor's catch shows this message. */
  if (!g) throw new Error('Canvas 2D bağlamı açılamadı.');
  g.clearRect(0, 0, W, H);
  g.drawImage(im, 0, 0, W, H);
  const d = g.getImageData(0, 0, W, H).data;
  const mask = new Uint8Array(W * H);
  for (let i = 0, px = 0; i < d.length; i += 4, px += 1) {
    if ((d[i + 3] ?? 0) < 24) continue;
    if (!((d[i] ?? 0) > 243 && (d[i + 1] ?? 0) > 243 && (d[i + 2] ?? 0) > 243)) mask[px] = 1;
  }
  const out: AssetAnalysis = { w: im.naturalWidth, h: im.naturalHeight, gw: W, gh: H, mask };
  analysisCache.set(key, out);
  return out;
}

/** How much of a 0–1 box is ink. The box is clamped to the grid, never wrapped around it. */
export function inkInBox(analysis: AssetAnalysis, box: InkBox): InkMeasure {
  const { gw, gh, mask } = analysis;
  const x0 = clamp(Math.floor(box.left * gw), 0, gw - 1);
  const x1 = clamp(Math.ceil(box.right * gw), 0, gw);
  const y0 = clamp(Math.floor(box.top * gh), 0, gh - 1);
  const y1 = clamp(Math.ceil(box.bottom * gh), 0, gh);
  let ink = 0;
  let total = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      total++;
      if (mask[y * gw + x]) ink++;
    }
  }
  return { ink, total, ratio: total ? ink / total : 0 };
}

/** The drawn artwork's bounding box in grid cells, or null when the background is blank. */
export function artworkBBox(analysis: AssetAnalysis): ArtworkBBox | null {
  const { gw, gh, mask } = analysis;
  let top = -1;
  let bottom = -1;
  let left = gw;
  let right = -1;
  for (let y = 0; y < gh; y++) {
    for (let x = 0; x < gw; x++) {
      if (mask[y * gw + x]) {
        if (top < 0) top = y;
        bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (top < 0) return null;
  return { top, bottom, left, right };
}

interface RowRun {
  y0: number;
  y1: number;
}

/**
 * The widest run of empty rows INSIDE the artwork.
 *
 * Only the middle 22–78% of the width is scanned, so a full-height border or a side ornament does
 * not disqualify every row; `tol` (0.4% of that window, at least one cell) lets a stray antialiased
 * pixel through, because an exact-zero test finds no band on a real scanned certificate.
 *
 * Runs that touch the artwork's top or bottom edge are preferred away from: a band hanging off the
 * end of the drawing is margin, not a place to put a name. `cands` is only fallen back to when the
 * artwork has no interior gap at all.
 */
export function detectSafeBand(analysis: AssetAnalysis, opts: SafeBandOptions = {}): SafeBand | null {
  const { gw, gh, mask } = analysis;
  const bbox = artworkBBox(analysis);
  if (!bbox) return null;
  const minY = opts.minY ?? 0.05;
  const maxY = opts.maxY ?? 0.995;
  const cx0 = Math.floor((opts.centerLeft ?? 0.22) * gw);
  const cx1 = Math.ceil((opts.centerRight ?? 0.78) * gw);
  const rowInk: number[] = [];
  for (let y = 0; y < gh; y++) {
    let n = 0;
    for (let x = cx0; x < cx1; x++) if (mask[y * gw + x]) n++;
    rowInk.push(n);
  }
  const tol = Math.max(1, Math.round((cx1 - cx0) * 0.004));
  const cands: RowRun[] = [];
  let run: RowRun | null = null;
  const yFrom = Math.max(Math.floor(minY * gh), bbox.top + 1);
  const yTo = Math.min(Math.ceil(maxY * gh), bbox.bottom);
  for (let y = yFrom; y < yTo; y++) {
    if ((rowInk[y] ?? 0) <= tol) {
      if (!run) run = { y0: y, y1: y };
      else run.y1 = y;
    } else {
      if (run) cands.push(run);
      run = null;
    }
  }
  if (run) cands.push(run);
  const inside = cands.filter((r) => r.y0 > bbox.top && r.y1 < bbox.bottom);
  const pool = inside.length ? inside : cands;
  let best: RowRun | null = null;
  for (const r of pool) {
    const h = r.y1 - r.y0;
    const bh = best ? best.y1 - best.y0 : -1;
    /* Ties go to the LOWER band — upstream's `r.y0 > best.y0`, which only ever runs with a `best`
       in hand, since the first candidate always wins on height against the -1 seed. */
    if (h > bh || (best !== null && h === bh && r.y0 > best.y0)) best = r;
  }
  if (!best) return null;
  const mid = Math.round((best.y0 + best.y1) / 2);
  const cxMid = Math.round(gw / 2);
  const rowsInBand = (x: number): boolean => {
    for (let y = best.y0; y <= best.y1; y++) if (mask[y * gw + x]) return true;
    return false;
  };
  /* The band's width is grown outwards from the centre column until it meets ink on either side. */
  let left = cxMid;
  let right = cxMid;
  while (left > 0 && !rowsInBand(left - 1)) left--;
  while (right < gw - 1 && !rowsInBand(right + 1)) right++;
  return {
    top: best.y0 / gh,
    bottom: (best.y1 + 1) / gh,
    center: (mid + 0.5) / gh,
    height: (best.y1 - best.y0 + 1) / gh,
    left: left / gw,
    right: (right + 1) / gw,
    width: (right - left + 1) / gw,
  };
}

/**
 * A band turned into layer settings.
 *
 * The 10% padding is inset from all four sides, so the text never sits against the ink it was
 * placed between. The font size is the smaller of two estimates: `.62` of the band's usable height
 * converted through the artwork's aspect ratio (ratios are of the canvas WIDTH, the band's of its
 * height), and the width a 20-character name at an average `.52` em advance would need. The result
 * is clamped to 1.2%–16% of the canvas width, and the minimum size to 45% of it, so shrink-to-fit
 * still has somewhere to go for a long name.
 */
export function layerFromBand(band: SafeBand, analysis: AssetAnalysis): BandLayerConfig {
  const pad = 0.1;
  const usableH = band.height * (1 - pad * 2);
  const usableW = band.width * (1 - pad * 2);
  const aspect = analysis.h / analysis.w;
  const byHeight = usableH * aspect * 0.62;
  const byWidth = usableW / (20 * 0.52);
  const fontSizeRatio = clamp(Math.min(byHeight, byWidth), 0.012, 0.16);
  return {
    x: (band.left + band.right) / 2,
    y: band.center,
    w: clamp(usableW, 0.2, 0.96),
    fontSizeRatio: +fontSizeRatio.toFixed(5),
    minFontSizeRatio: +(fontSizeRatio * 0.45).toFixed(5),
    lineHeight: 1.12,
    maxLines: 1,
    safeArea: {
      x: +band.left.toFixed(4),
      y: +band.top.toFixed(4),
      width: +(band.right - band.left).toFixed(4),
      height: +(band.bottom - band.top).toFixed(4),
    },
  };
}

// --------------------------------------------------------------- «Çakışmayı ölç (5 değer)»

/** `m01b-assets.js:594` — the five names the chip measures a person layer against. */
export const TEST_NAMES: readonly string[] = [
  'Ali Can',
  'Ayşe Yılmaz',
  'Mehmet Ali Büyükdemircioğlu',
  'Alexandra Konstantinopoulou-Wetherington',
  'İbrahim Çağrı Şentürk',
];

/** The organisation half of the same set; the chip picks by `layout.subjectType` (`m08-admin.js:403`). */
export const TEST_ORGS: readonly string[] = [
  'ABC Holding',
  'Kolektif Alan',
  'Uluslararası Anadolu Lojistik ve Dağıtım Hizmetleri A.Ş.',
  'XYZ Teknoloji',
  'Şerbet Gıda ve İçecek Sanayi',
];

export interface FitCheckSafeArea {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** `Layout` as the plan reads it, plus the safe areas only the editor's layout carries. */
export interface FitCheckLayout extends Layout {
  safeAreas?: FitCheckSafeArea[] | undefined;
}

export interface FitCheckLayer extends Layer {
  safeAreaId?: string | null | undefined;
}

export interface FitCheckOptions {
  threshold?: number | undefined;
}

export interface FitCheckRow {
  value: string;
  size: number;
  overflow: boolean;
  lines: number;
  box: InkBox;
  inkRatio: number;
  collision: boolean;
  /** null when the layer is bound to no safe area — «not measured», never «passed». */
  inSafe: boolean | null;
  inCanvas: boolean;
  ok: boolean;
}

export interface FitCheckResult {
  ok: boolean;
  results: FitCheckRow[];
  hasBackground: boolean;
  safeAreaId: string | null;
}

/**
 * `verifyLayerPlacement` — measure one layer against five sample values.
 *
 * Each value is fitted exactly as the renderer would fit it, its drawn box is derived from the fit,
 * and that box is weighed against the background's ink. `.006` of slack on the safe-area test is
 * upstream's: the box comes from a font measurement and the safe area from a rounded band, so an
 * exact containment test would fail on the placement this file just produced.
 *
 * Without a background there is no ink to collide with, so `ratio` is 0 and the row reports overflow
 * only — the caller's footer says which of the two measurements actually ran.
 */
export async function verifyLayerPlacement(
  layout: FitCheckLayout,
  layer: FitCheckLayer,
  values: readonly string[],
  opts: FitCheckOptions = {},
): Promise<FitCheckResult> {
  const canvas: Canvas = layout.canvas;
  const ref: AssetRef | null | undefined = layout.background?.assetRef;
  const res = ref ? resolveAssetRef(ref) : ({ ok: false, reason: 'missing_ref' } as const);
  const bg = res.ok ? res.asset.dataUrlForPrototype : null;
  const analysis = bg && ref?.assetId ? await analyzeAsset(bg, ref.assetId) : null;
  const safe = (layout.safeAreas || []).find((s) => s.id === layer.safeAreaId) || null;
  const out: FitCheckRow[] = [];
  for (const val of values) {
    const r: Fit = fitText(layer, val, canvas);
    const cw = canvasW(canvas);
    const chh = canvasH(canvas);
    /* `Engine.geometry`'s text branch (`m04-engine.js:90`); `textGeometry` is module-private in
       `render/engine.ts`, and only its `top`/`blockH` are needed to build the box. */
    const lh = r.size * (layer.lineHeight || 1.15);
    const blockH = r.lines.length * lh;
    const yPx = layer.y * chh;
    const top =
      layer.verticalAlign === 'top'
        ? yPx
        : layer.verticalAlign === 'bottom'
          ? yPx - blockH
          : yPx - blockH / 2;
    const boxTop = top / chh;
    const boxBottom = (top + blockH) / chh;
    const halfW = r.width / 2 / cw;
    /* `layer.x` is the anchor, not the centre: left-aligned text grows right of it, right-aligned
       left of it, so the box has to be recentred before it can be compared with anything. */
    const cx = layer.align === 'left' ? layer.x + halfW : layer.align === 'right' ? layer.x - halfW : layer.x;
    const box: InkBox = { left: cx - halfW, right: cx + halfW, top: boxTop, bottom: boxBottom };
    const ink = analysis ? inkInBox(analysis, box) : { ratio: 0 };
    const inSafe = safe
      ? box.top >= safe.y - 0.006 &&
        box.bottom <= safe.y + safe.height + 0.006 &&
        box.left >= safe.x - 0.006 &&
        box.right <= safe.x + safe.width + 0.006
      : null;
    const inCanvas = box.left >= -0.001 && box.right <= 1.001 && box.top >= -0.001 && box.bottom <= 1.001;
    const collision = ink.ratio > (opts.threshold ?? 0.004);
    out.push({
      value: val,
      size: r.size,
      overflow: r.overflow,
      lines: r.lines.length,
      box,
      inkRatio: +ink.ratio.toFixed(4),
      collision,
      inSafe,
      inCanvas,
      ok: !r.overflow && !collision && inCanvas && inSafe !== false,
    });
  }
  return {
    ok: out.every((x) => x.ok),
    results: out,
    hasBackground: !!bg,
    safeAreaId: layer.safeAreaId || null,
  };
}

/** The chip's own name (`m08-admin.js:342`, id `checkFit`); the function is `verifyLayerPlacement`. */
export const checkFit = verifyLayerPlacement;
